/**
 * ODT (OpenDocument Text) conversion utilities.
 * Converts between HTML (used by CKEditor) and ODT format for template import/export.
 */

import JSZip from 'jszip';

const ODF_NS = {
  office: 'urn:oasis:names:tc:opendocument:xmlns:office:1.0',
  text: 'urn:oasis:names:tc:opendocument:xmlns:text:1.0',
  style: 'urn:oasis:names:tc:opendocument:xmlns:style:1.0',
  fo: 'urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0',
  table: 'urn:oasis:names:tc:opendocument:xmlns:table:1.0',
};

/**
 * Convert ODT file (ArrayBuffer) to HTML string.
 * @param {ArrayBuffer} arrayBuffer - ODT file content
 * @returns {Promise<string>} HTML string
 */
export async function odtToHtml(arrayBuffer) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const contentFile = zip.file('content.xml');
  if (!contentFile) {
    throw new Error('Invalid ODT: content.xml not found');
  }
  const xmlStr = await contentFile.async('string');
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlStr, 'text/xml');
  const officeText = doc.getElementsByTagNameNS(ODF_NS.office, 'text')[0];
  if (!officeText) {
    return '';
  }
  return odfBodyToHtml(officeText);
}

function odfBodyToHtml(node) {
  const parts = [];
  for (let i = 0; i < node.childNodes.length; i++) {
    const child = node.childNodes[i];
    if (child.nodeType !== 1) continue;
    const local = child.localName;
    const ns = child.namespaceURI;
    if (ns === ODF_NS.text) {
      if (local === 'p') {
        parts.push('<p>' + odfInlineToHtml(child) + '</p>');
      } else if (local === 'h') {
        const level = parseInt(child.getAttributeNS(ODF_NS.text, 'outline-level') || '1', 10);
        const h = Math.min(6, Math.max(1, level));
        parts.push(`<h${h}>` + odfInlineToHtml(child) + `</h${h}>`);
      } else if (local === 'list') {
        parts.push(odfListToHtml(child));
      } else if (local === 'sequence-decls' || local === 'sequence-decl') {
        // skip
      } else {
        parts.push(odfInlineToHtml(child));
      }
    } else if (ns === ODF_NS.table && local === 'table') {
      parts.push(odfTableToHtml(child));
    }
  }
  return parts.join('');
}

function odfInlineToHtml(node) {
  const parts = [];
  for (let i = 0; i < node.childNodes.length; i++) {
    const child = node.childNodes[i];
    if (child.nodeType === 3) {
      parts.push(escapeHtml(child.textContent));
    } else if (child.nodeType === 1) {
      const local = child.localName;
      const ns = child.namespaceURI;
      if (ns === ODF_NS.text) {
        if (local === 'span') {
          const style = child.getAttributeNS(ODF_NS.text, 'style-name') || '';
          const content = odfInlineToHtml(child);
          if (/bold|Bold/i.test(style)) {
            parts.push('<strong>' + content + '</strong>');
          } else if (/italic|Italic/i.test(style)) {
            parts.push('<em>' + content + '</em>');
          } else if (/underline|Underline/i.test(style)) {
            parts.push('<u>' + content + '</u>');
          } else if (/strikethrough|Strikethrough/i.test(style)) {
            parts.push('<s>' + content + '</s>');
          } else {
            parts.push('<span>' + content + '</span>');
          }
        } else if (local === 's') {
          const c = parseInt(child.getAttributeNS(ODF_NS.text, 'c') || '1', 10);
          parts.push(' '.repeat(Math.min(100, Math.max(1, c))));
        } else if (local === 'tab') {
          parts.push('\t');
        } else if (local === 'line-break') {
          parts.push('<br/>');
        } else {
          parts.push(odfInlineToHtml(child));
        }
      }
    }
  }
  return parts.join('');
}

function odfListToHtml(node) {
  const tag = node.getAttributeNS(ODF_NS.text, 'style-name');
  const isOrdered = /numbered|Numbered|1\s|L1/i.test(tag || '');
  const listTag = isOrdered ? 'ol' : 'ul';
  let html = '<' + listTag + '>';
  const items = node.getElementsByTagNameNS(ODF_NS.text, 'list-item');
  for (let i = 0; i < items.length; i++) {
    let itemHtml = '';
    for (let j = 0; j < items[i].childNodes.length; j++) {
      const c = items[i].childNodes[j];
      if (c.nodeType === 1 && c.localName === 'p' && c.namespaceURI === ODF_NS.text) {
        itemHtml += odfInlineToHtml(c);
      } else if (c.nodeType === 1 && c.localName === 'list' && c.namespaceURI === ODF_NS.text) {
        itemHtml += odfListToHtml(c);
      }
    }
    html += '<li>' + itemHtml + '</li>';
  }
  html += '</' + listTag + '>';
  return html;
}

function odfTableToHtml(node) {
  let html = '<table><tbody>';
  const rows = node.getElementsByTagNameNS(ODF_NS.table, 'table-row');
  for (let r = 0; r < rows.length; r++) {
    html += '<tr>';
    const cells = rows[r].getElementsByTagNameNS(ODF_NS.table, 'table-cell');
    for (let c = 0; c < cells.length; c++) {
      const cell = cells[c];
      const repeat = parseInt(cell.getAttributeNS(ODF_NS.table, 'number-columns-repeated') || '1', 10);
      const tag = r === 0 ? 'th' : 'td';
      let cellHtml = '';
      const paras = cell.getElementsByTagNameNS(ODF_NS.text, 'p');
      for (let p = 0; p < paras.length; p++) {
        cellHtml += odfInlineToHtml(paras[p]);
        if (p < paras.length - 1) cellHtml += '<br/>';
      }
      for (let rep = 0; rep < repeat; rep++) {
        html += '<' + tag + '>' + cellHtml + '</' + tag + '>';
      }
    }
    html += '</tr>';
  }
  html += '</tbody></table>';
  return html;
}

function escapeHtml(s) {
  if (typeof s !== 'string') return '';
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeXml(s) {
  return escapeHtml(s);
}

/**
 * Convert HTML string to ODT file (ArrayBuffer).
 * @param {string} html - HTML content
 * @returns {Promise<ArrayBuffer>} ODT file as ArrayBuffer
 */
export async function htmlToOdt(html) {
  const contentXml = htmlToOdfContent(html || '');
  const zip = new JSZip();

  zip.file('mimetype', 'application/vnd.oasis.opendocument.text', {
    compression: 'STORE',
    compressionOptions: { level: 0 },
  });

  zip.file('content.xml', contentXml);
  zip.file('styles.xml', MINIMAL_STYLES);
  zip.file('meta.xml', MINIMAL_META);
  zip.file('META-INF/manifest.xml', MINIMAL_MANIFEST);

  const blob = await zip.generateAsync({
    type: 'arraybuffer',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
  return blob;
}

function htmlToOdfContent(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    '<div xmlns="http://www.w3.org/1999/xhtml">' + html + '</div>',
    'text/html'
  );
  const container = doc.body?.firstElementChild || doc.body || doc.createElement('div');
  let odfBody = '';
  const children = container ? (container.tagName === 'DIV' ? container.childNodes : [container]) : [];
  for (let i = 0; i < children.length; i++) {
    const c = children[i];
    if (c.nodeType === 1) odfBody += walkHtmlToOdf(c);
    else if (c.nodeType === 3 && (c.textContent || '').trim()) {
      odfBody += '<text:p>' + escapeXml(c.textContent) + '</text:p>';
    }
  }
  return `<?xml version="1.0" encoding="UTF-8"?>
<office:document-content xmlns:office="${ODF_NS.office}" xmlns:text="${ODF_NS.text}" xmlns:style="${ODF_NS.style}" xmlns:fo="${ODF_NS.fo}" xmlns:table="${ODF_NS.table}" office:version="1.2">
  <office:body>
    <office:text>${odfBody}</office:text>
  </office:body>
</office:document-content>`;
}

function walkHtmlToOdf(node) {
  if (!node) return '';
  const parts = [];
  const tag = (node.nodeName || '').toLowerCase();
  if (tag === 'p' || tag === 'div') {
    const inner = walkHtmlToOdfChildren(node);
    if (inner || tag === 'p') parts.push('<text:p>' + inner + '</text:p>');
  } else if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
    const level = parseInt(tag.charAt(1), 10) || 1;
    parts.push('<text:h text:outline-level="' + level + '">' + walkHtmlToOdfChildren(node) + '</text:h>');
  } else if (tag === 'br') {
    return '<text:line-break/>';
  } else if (tag === 'ul' || tag === 'ol') {
    const items = node.querySelectorAll(':scope > li');
    let listXml = '<text:list>';
    items.forEach((li) => {
      const inner = walkHtmlToOdfChildren(li);
      listXml += '<text:list-item><text:p>' + inner + '</text:p></text:list-item>';
    });
    listXml += '</text:list>';
    return listXml;
  } else if (tag === 'li') {
    return walkHtmlToOdfChildren(node);
  } else if (tag === 'strong' || tag === 'b') {
    return '<text:span text:style-name="T1">' + walkHtmlToOdfChildren(node) + '</text:span>';
  } else if (tag === 'em' || tag === 'i') {
    return '<text:span text:style-name="T2">' + walkHtmlToOdfChildren(node) + '</text:span>';
  } else if (tag === 'u') {
    return '<text:span text:style-name="T3">' + walkHtmlToOdfChildren(node) + '</text:span>';
  } else if (tag === 's' || tag === 'strike') {
    return '<text:span text:style-name="T4">' + walkHtmlToOdfChildren(node) + '</text:span>';
  } else if (tag === 'span') {
    return walkHtmlToOdfChildren(node);
  } else if (tag === 'table') {
    return htmlTableToOdf(node);
  } else if (tag === 'tbody' || tag === 'thead') {
    return walkHtmlToOdfChildren(node);
  } else if (tag === 'tr') {
    const cells = node.querySelectorAll(':scope > td, :scope > th');
    let rowXml = '<table:table-row>';
    cells.forEach((cell) => {
      const inner = walkHtmlToOdfChildren(cell);
      const pContent = inner ? inner : '';
      rowXml += '<table:table-cell office:value-type="string"><text:p>' + pContent + '</text:p></table:table-cell>';
    });
    rowXml += '</table:table-row>';
    return rowXml;
  } else if (tag === 'td' || tag === 'th') {
    return walkHtmlToOdfChildren(node);
  } else {
    return walkHtmlToOdfChildren(node);
  }
  return parts.join('');
}

function walkHtmlToOdfChildren(node) {
  let s = '';
  for (let i = 0; i < node.childNodes.length; i++) {
    const c = node.childNodes[i];
    if (c.nodeType === 3) {
      s += escapeXml(c.textContent || '');
    } else if (c.nodeType === 1) {
      const one = walkHtmlToOdf(c);
      if (one) s += one;
    }
  }
  return s;
}

function htmlTableToOdf(table) {
  const rows = table.querySelectorAll('tr');
  let xml = '<table:table table:name="Table1">';
  rows.forEach((tr) => {
    xml += walkHtmlToOdf(tr);
  });
  xml += '</table:table>';
  return xml;
}

const MINIMAL_STYLES = `<?xml version="1.0" encoding="UTF-8"?>
<office:document-styles xmlns:office="${ODF_NS.office}" xmlns:style="${ODF_NS.style}" xmlns:fo="${ODF_NS.fo}" xmlns:text="${ODF_NS.text}" office:version="1.2">
  <office:styles>
    <style:default-style style:family="paragraph">
      <style:paragraph-properties fo:orphans="2" fo:widows="2"/>
      <style:text-properties fo:font-size="12pt" style:font-size-asian="12pt"/>
    </style:default-style>
    <style:style style:name="T1" style:family="text">
      <style:text-properties fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="T2" style:family="text">
      <style:text-properties fo:font-style="italic"/>
    </style:style>
    <style:style style:name="T3" style:family="text">
      <style:text-properties style:text-underline-style="solid"/>
    </style:style>
    <style:style style:name="T4" style:family="text">
      <style:text-properties style:text-line-through-style="solid"/>
    </style:style>
  </office:styles>
</office:document-styles>`;

const MINIMAL_META = `<?xml version="1.0" encoding="UTF-8"?>
<office:document-meta xmlns:office="${ODF_NS.office}" office:version="1.2">
  <office:meta/>
</office:document-meta>`;

const MINIMAL_MANIFEST = `<?xml version="1.0" encoding="UTF-8"?>
<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0">
  <manifest:file-entry manifest:full-path="/" manifest:media-type="application/vnd.oasis.opendocument.text"/>
  <manifest:file-entry manifest:full-path="content.xml" manifest:media-type="text/xml"/>
  <manifest:file-entry manifest:full-path="styles.xml" manifest:media-type="text/xml"/>
  <manifest:file-entry manifest:full-path="meta.xml" manifest:media-type="text/xml"/>
  <manifest:file-entry manifest:full-path="mimetype" manifest:media-type="text/plain"/>
</manifest:manifest>`;
